import React, { Fragment, useEffect, useState } from "react";

import { Dialog, Transition } from "@headlessui/react";
import { Check, Code2, FileImage, LayoutTemplate } from "lucide-react";
import { useTranslation } from "react-i18next";

import { XIcon } from "../../../components/icons";
import { PRIMARY_BUTTON_CLASS } from "../utils/styles";

export const CERTIFICATE_DESIGN_TYPE = {
  SVG: "svg",
  HTML: "html",
};

export const CERTIFICATE_ONBOARDING_ROUTES = {
  [CERTIFICATE_DESIGN_TYPE.SVG]: "/issuer/tenant/certificate-onboarding",
  [CERTIFICATE_DESIGN_TYPE.HTML]: "/issuer/tenant/certificate-onboarding/html",
};

const OPTIONS = [
  {
    id: CERTIFICATE_DESIGN_TYPE.HTML,
    Icon: LayoutTemplate,
    titleKey: "certificateOnboarding:chooser_html_title",
    descriptionKey: "certificateOnboarding:chooser_html_description",
    bulletKeys: [
      "certificateOnboarding:chooser_html_bullet_1",
      "certificateOnboarding:chooser_html_bullet_2",
      "certificateOnboarding:chooser_html_bullet_3",
    ],
  },
  {
    id: CERTIFICATE_DESIGN_TYPE.SVG,
    Icon: FileImage,
    titleKey: "certificateOnboarding:chooser_svg_title",
    descriptionKey: "certificateOnboarding:chooser_svg_description",
    bulletKeys: [
      "certificateOnboarding:chooser_svg_bullet_1",
      "certificateOnboarding:chooser_svg_bullet_2",
      "certificateOnboarding:chooser_svg_bullet_3",
    ],
  },
];

function CertificateTypeChooserModal({ isOpen, onClose, onSelect }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(CERTIFICATE_DESIGN_TYPE.HTML);

  useEffect(() => {
    if (isOpen) setSelected(CERTIFICATE_DESIGN_TYPE.HTML);
  }, [isOpen]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={onClose}
        id="cert-type-chooser-modal"
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm"
            aria-hidden
          />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-4xl transform overflow-hidden rounded-2xl border border-cfCardBorder bg-cfSecondary shadow-2xl transition-all">
                <div className="relative overflow-hidden bg-gradient-to-br from-cfPrimaryButton via-cfPrimary to-cfPrimaryLight px-6 py-6 sm:px-8 text-cfSecondary">
                  <div
                    className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/10"
                    aria-hidden
                  />
                  <div
                    className="pointer-events-none absolute -left-6 -bottom-10 h-24 w-24 rounded-full bg-white/10"
                    aria-hidden
                  />
                  <button
                    type="button"
                    onClick={onClose}
                    id="cert-type-chooser-close"
                    className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-cfSecondary hover:bg-white/25 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                    aria-label={t("certificateOnboarding:chooser_close")}
                  >
                    <XIcon size={16} className="inline-flex" aria-hidden />
                  </button>
                  <Dialog.Title className="relative text-xl font-semibold tracking-tight pr-10">
                    {t("certificateOnboarding:chooser_title")}
                  </Dialog.Title>
                  <p className="relative mt-1.5 text-sm text-white/90 leading-relaxed max-w-xl">
                    {t("certificateOnboarding:chooser_subtitle")}
                  </p>
                </div>

                <div
                  className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 sm:p-8"
                  role="radiogroup"
                  aria-label={t("certificateOnboarding:chooser_title")}
                >
                  {OPTIONS.map((option) => {
                    const isSelected = selected === option.id;
                    const { Icon } = option;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        id={`cert-type-chooser-${option.id}`}
                        onClick={() => setSelected(option.id)}
                        onDoubleClick={() => onSelect(option.id)}
                        className={`relative text-left rounded-xl border p-4 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary ${
                          isSelected
                            ? "border-2 border-cfPrimary bg-cfPrimaryMuted shadow-sm"
                            : "border-cfCardBorder bg-cfSecondary hover:border-cfPrimaryBorder hover:bg-cfCardHover"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div
                            className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center transition-colors ${
                              isSelected
                                ? "bg-gradient-to-br from-cfPrimaryButton via-cfPrimary to-cfPrimaryLight text-cfSecondary"
                                : "bg-cfInactiveSurface text-cfPrimary"
                            }`}
                          >
                            <Icon
                              className="w-5 h-5"
                              strokeWidth={1.75}
                              aria-hidden
                            />
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-base font-semibold text-cfHeadingText">
                            {t(option.titleKey)}
                          </h3>
                          {option.badgeKey && (
                            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-cfPrimary text-cfSecondary">
                              {t(option.badgeKey)}
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-sm text-cfGlobalText leading-relaxed">
                          {t(option.descriptionKey)}
                        </p>
                        <ul className="mt-3 space-y-1.5">
                          {option.bulletKeys.map((key) => (
                            <li
                              key={key}
                              className="flex items-start gap-2 text-xs text-cfGlobalText"
                            >
                              <Check
                                className="w-3.5 h-3.5 mt-0.5 text-cfPrimary shrink-0"
                                strokeWidth={2.5}
                                aria-hidden
                              />
                              {t(key)}
                            </li>
                          ))}
                        </ul>
                      </button>
                    );
                  })}
                </div>

                <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 px-4 pb-4 sm:px-8 sm:pb-8">
                  <p className="flex items-center gap-2 text-xs text-cfGlobalText">
                    <Code2
                      className="w-4 h-4 text-cfPrimary shrink-0"
                      aria-hidden
                    />
                    {t("certificateOnboarding:chooser_footer_hint")}
                  </p>
                  <div className="flex flex-col-reverse sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={onClose}
                      id="cert-type-chooser-cancel"
                      className="px-4 py-2 border border-cfCardBorder text-cfHeadingText font-medium rounded-md hover:bg-cfCardHover transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary"
                    >
                      {t("certificateOnboarding:chooser_cancel")}
                    </button>
                    <button
                      type="button"
                      onClick={() => onSelect(selected)}
                      id="cert-type-chooser-continue"
                      className={`${PRIMARY_BUTTON_CLASS} inline-flex items-center justify-center gap-2`}
                    >
                      {t("certificateOnboarding:chooser_continue")}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default CertificateTypeChooserModal;
